import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, StatusPill } from "@/components/common";
import { api, errMsg, fmtMoney, fmtDateTime } from "@/lib/api";

export default function Payments() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [review, setReview] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/payments", { params: { status: status === "all" ? "" : status } }).then((r) => setRows(r.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const decide = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/payments/${review.p.id}/${review.action}`, { note });
      toast.success(review.action === "approve" ? "Payment approved and access granted" : "Payment rejected");
      setReview(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Verification queue" title="Payments" subtitle="Verify the money actually arrived in your UPI/bank account before approving. Approval is one-time and cannot be duplicated." />
      <Tabs value={status} onValueChange={setStatus} className="mb-4">
        <TabsList><TabsTrigger value="pending" data-testid="payments-tab-pending">Pending</TabsTrigger><TabsTrigger value="approved" data-testid="payments-tab-approved">Approved</TabsTrigger><TabsTrigger value="rejected" data-testid="payments-tab-rejected">Rejected</TabsTrigger><TabsTrigger value="all" data-testid="payments-tab-all">All</TabsTrigger></TabsList>
      </Tabs>
      <div className="bg-card rounded-xl border overflow-x-auto fade-up">
        <Table data-testid="admin-payments-table">
          <TableHeader><TableRow><TableHead>Agency</TableHead><TableHead>Item</TableHead><TableHead>Amount</TableHead><TableHead>Transaction ID</TableHead><TableHead>Submitted</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`admin-payment-row-${p.id}`}>
                <TableCell className="min-w-[160px]"><p className="font-medium">{p.agency_name}</p><p className="text-xs text-muted-foreground">{p.user_email}</p></TableCell>
                <TableCell className="text-sm">{p.item_name}<p className="text-xs text-muted-foreground">{p.kind === "plan" ? `${p.duration_days}-day plan` : `${p.credits} credits`}</p></TableCell>
                <TableCell className="font-semibold whitespace-nowrap">{fmtMoney(p.amount)}</TableCell>
                <TableCell className="font-mono text-xs">{p.transaction_id}{p.note && <p className="font-sans text-muted-foreground mt-1">{p.note}</p>}</TableCell>
                <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(p.created_at)}</TableCell>
                <TableCell><StatusPill status={p.status} testId={`admin-payment-status-${p.id}`} /></TableCell>
                <TableCell className="whitespace-nowrap">
                  {p.status === "pending" && <div className="flex gap-2">
                    <Button size="sm" onClick={() => setReview({ p, action: "approve" })} data-testid={`admin-approve-payment-${p.id}`}><Check className="h-4 w-4 mr-1" />Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => setReview({ p, action: "reject" })} data-testid={`admin-reject-payment-${p.id}`}><X className="h-4 w-4 mr-1" />Reject</Button>
                  </div>}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No payment requests</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent data-testid="payment-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display capitalize">{review?.action} payment</DialogTitle>
            <DialogDescription>{review && `${review.p.agency_name} · ${fmtMoney(review.p.amount)} · Txn ${review.p.transaction_id}`}</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Admin note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-review-note" />
          <Button disabled={busy} variant={review?.action === "reject" ? "destructive" : "default"} onClick={decide} data-testid="payment-review-confirm">Confirm {review?.action}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
